import React, { FC, useRef, useState } from "react";
import {
  Alert,
  Divider,
  Modal,
  ModalActions,
  ModalContent,
  ModalTitle,
} from "@carrier-io/air-react";
import Button from "@carrier-io/air-react/Button";
import Checkbox from "@carrier-io/air-react/Checkbox";
import FormControlLabel from "@carrier-io/air-react/FormControlLabel";
import TabContext from "@carrier-io/air-react/TabContext";
import Tabs from "@carrier-io/air-react/Tabs";
import Tab from "@carrier-io/air-react/Tab";
import TabPanel from "@carrier-io/air-react/TabPanel";
import Grid from "@carrier-io/air-react/Grid";
import Paper from "@carrier-io/air-react/Paper";
import { LinearProgress } from "@carrier-io/air-react";
import CheckIcon from "@mui/icons-material/Check";
import AlertTitle from "@carrier-io/air-react/AlertTitle";
import { getCurrentUser } from "../../utils/cookie";
import { ConsentRenderFileContent } from "./ConsentRenderFileContent";
import { Clients, useMutation } from "../../apollo";
import { PutConsentMutation } from "../../graphql/mutations/putConsent";
import { useEffect } from "react";

type Consent = {
  title: string;
  file: string;
};

type ConsentModalProps = {
  consents: Consent[];
  onSuccess: () => void;
  onError: (error: Error) => void;
  onDecline: () => void;
};

export const ConsentModal: FC<ConsentModalProps> = ({
  consents,
  onSuccess,
  onError,
  onDecline,
}) => {
  const [activeTab, setActiveTab] = useState(consents[0]?.file);
  const [acceptedTabs, setAcceptedTabs] = useState<string[]>([]);
  const [checkedTabs, setCheckedTabs] = useState<string[]>([]);
  const rootRef = useRef<HTMLDivElement>(null);
  const currentUser = getCurrentUser();
  const [declineClickedOnce, setDeclineClickedOnce] = useState(false);
  const [assignConsent, assignConsentResult] = useMutation(
    Clients.UPS,
    PutConsentMutation,
  );

  if (!currentUser?.username || !currentUser?.region) {
    onError(new Error("User not found"));
    return null;
  }

  const user = {
    username: currentUser.username,
    region: currentUser.region,
  };

  const handleDecline = async (file: string) => {
    if (declineClickedOnce) {
      onDecline();
      return;
    }

    await assignConsent({
      variables: {
        consentInput: {
          username: user.username,
          region: user.region,
          name: file,
          accepted: false,
        },
      },
    }).catch(onDecline);

    setAcceptedTabs((prev) => prev.filter((f) => f !== file));

    setDeclineClickedOnce(true);
  };

  const handleAccept = async (file: string) => {
    await assignConsent({
      variables: {
        username: user.username,
        region: user.region,
        consentInput: {
          username: user.username,
          region: user.region,
          name: file,
          accepted: true,
        },
      },
    });

    setAcceptedTabs((prev) => [...prev, file]);
  };

  const handleChange = (item: Consent) =>
    setCheckedTabs((prev) =>
      prev.includes(item.file)
        ? prev.filter((f) => f !== item.file)
        : [...prev, item.file],
    );

  useEffect(() => {
    const missing = consents.find((i) => !acceptedTabs.includes(i.file));
    if (missing) {
      setActiveTab(missing.file);
    } else {
      onSuccess();
    }
  }, [acceptedTabs, consents, onSuccess]);

  if (consents.length === 0) {
    return null;
  }

  return (
    <Modal
      open={true}
      aria-labelledby="server-modal-title"
      aria-describedby="server-modal-description"
      maxWidth="xl"
      fullWidth
      sx={{
        display: "flex",
        p: 1,
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
      }}
      container={() => rootRef.current}
      onClose={onDecline}
      onAbort={onDecline}
    >
      <Paper style={{ width: Math.min(1280, window.innerWidth - 50) + "px" }}>
        {declineClickedOnce ? (
          <Alert
            role="alert"
            severity="error"
            variant="standard"
            style={{ margin: "20px" }}
            onClose={() => handleDecline(activeTab)}
          >
            <AlertTitle>Unable to Proceed</AlertTitle>
            Acceptance of Terms of Service and Privacy Policy is required in
            order to proceed.
          </Alert>
        ) : (
          <ModalTitle
            clearable
            style={{ padding: "24px 24px 16px" }}
            onClose={() => handleDecline(activeTab)}
          >
            Terms of Service & Privacy Policy
          </ModalTitle>
        )}
        <ModalContent sx={{ padding: "0px 24px !important" }}>
          <TabContext value={activeTab}>
            <Tabs
              indicatorVariant="line"
              indicatorWidth="content"
              value={activeTab}
              onChange={(_, newValue) => setActiveTab(newValue)}
            >
              {consents.map((item) => (
                <Tab
                  key={item.title}
                  label={item.title}
                  value={item.file}
                  sx={{
                    "&.MuiButtonBase-root": {
                      textTransform: "none",
                    },
                  }}
                />
              ))}
            </Tabs>

            <Divider variant="full"></Divider>
            {consents.map((item) => (
              <TabPanel
                key={item.title}
                value={item.file}
                sx={{
                  maxHeight: "60vh",
                  overflowY: "auto",
                  padding: "0px",
                }}
              >
                <ConsentRenderFileContent fileUrl={item.file} />
              </TabPanel>
            ))}
          </TabContext>
        </ModalContent>

        <Divider variant="full"></Divider>
        {consents.map((item) => (
          <ModalActions
            key={item.title}
            style={{
              display: activeTab !== item.file ? "none" : "block",
            }}
            sx={{
              display: "flex",
              flexDirection: "column",
              margin: "16px",
              gap: 1,
            }}
          >
            <Grid container justifyContent="space-between" alignItems="center">
              <Grid item>
                <FormControlLabel
                  control={<Checkbox disabled={assignConsentResult.loading} />}
                  label={`I acknowledge that I have read the above and agree to the ${item.title}`}
                  checked={checkedTabs.includes(item.file)}
                  onChange={() => handleChange(item)}
                />
              </Grid>
              <Grid item>
                <Grid container spacing={2}>
                  <Grid item>
                    <Button
                      variant="text"
                      color="secondary"
                      onClick={() => handleDecline(item.file)}
                      disabled={assignConsentResult.loading}
                    >
                      Decline
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button
                      variant="outlined"
                      onClick={() => handleAccept(item.file)}
                      disabled={
                        !checkedTabs.includes(item.file) ||
                        assignConsentResult.loading
                      }
                      startIcon={<CheckIcon />}
                    >
                      Accept & Continue
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </ModalActions>
        ))}
      </Paper>
      {assignConsentResult.loading && <LinearProgress />}
    </Modal>
  );
};
